<div class="section-divider d-none d-md-flex align-items-center gap-3 mb-2">
  <div class="flex-grow-1 border-top"></div>
  <span class="fw-semibold small text-uppercase text-muted px-1" style="letter-spacing:.08em;">🔥 {{ 'HEIZUNG_TITLE' | translate }}</span>
  <div class="flex-grow-1 border-top"></div>
</div>

<!-- Section header (styles.css pattern (a): one row on desktop, title + controls rows on phones) -->
<div class="dash-section-head">
  <div class="dash-section-title">
    <span class="badge bg-danger dash-live"><span [style.opacity]="liveDotOn ? '1' : '0'">●</span> LIVE</span>
    <span class="dash-section-name">{{ 'HEIZUNG_TITLE' | translate }}</span>
    <span *ngIf="froelingUpdatedAt" class="dash-section-age">· {{ 'UPDATED_AGO' | translate:{time: updatedAgo(froelingUpdatedAt)} }}</span>
  </div>
  <div class="dash-section-controls">
    <ng-container *ngTemplateOutlet="rangeButtons"></ng-container>
    <span *ngIf="rangeLoading" class="spinner-border spinner-border-sm text-muted d-none d-sm-inline-block" role="status"></span>
    <button *ngIf="froelingHistory.length" type="button" class="btn btn-sm btn-outline-secondary dash-icon-btn"
            [attr.aria-label]="'CHART_OPEN' | translate" (click)="openHeizungChart.emit()">📈</button>
  </div>
</div>

<!-- Error banner: pending errors red; errors that are no longer active (only not yet acknowledged) neutral -->
<div *ngIf="activeErrors.length > 0" class="alert alert-danger py-2 px-3 mb-2 d-flex align-items-start gap-2" style="font-size:.8rem;">
  <span class="flex-shrink-0 mt-1">⚠️</span>
  <div>
    <div *ngFor="let err of activeErrors" class="mb-1">
      <span class="fw-semibold">{{ err.code }}</span>
      <span>{{ err.message }}</span>
      <span *ngIf="err.timestamp" class="text-muted ms-1 dash-meta">{{ errTime(err.timestamp) }}</span>
    </div>
  </div>
</div>
<div *ngIf="pastErrors.length > 0" class="alert alert-secondary py-2 px-3 mb-2 d-flex align-items-start gap-2" style="font-size:.8rem;">
  <span class="flex-shrink-0 mt-1">ℹ️</span>
  <div>
    <div class="fw-semibold mb-1">{{ 'HEIZUNG_ERR_PAST_TITLE' | translate }}</div>
    <div *ngFor="let err of pastErrors" class="mb-1">
      <span class="fw-semibold">{{ err.code }}</span>
      <span>{{ err.message }}</span>
      <span *ngIf="err.timestamp" class="text-muted ms-1 dash-meta">{{ errTime(err.timestamp) }}</span>
      <span *ngIf="err.unacknowledged" class="text-muted ms-1 dash-meta">· {{ 'HEIZUNG_ERR_UNACK' | translate }}</span>
    </div>
  </div>
</div>

<!-- Range buttons (section header and card popups). While a range loads, phones show the spinner inside the
     pressed button instead of next to the group, so the full-width segmented control keeps its width; from
     576 px the spinner beside the group remains (as on Energie). -->
<ng-template #rangeButtons>
  <div class="btn-group btn-group-sm dash-seg dash-seg-xs" role="group">
    <button *ngFor="let r of chartRanges" type="button" class="btn"
            [ngClass]="range === r.key ? 'btn-secondary' : 'btn-outline-secondary'"
            [class.range-busy]="rangeLoading && range === r.key"
            (click)="setRange(r.key)"><span class="range-label">{{ r.label | translate }}</span><span
            *ngIf="rangeLoading && range === r.key" class="spinner-border range-spin" aria-hidden="true"></span></button>
  </div>
</ng-template>

<!-- Card popups (the card's name is the popup's title, [popupTitle]; 📌 puts its chart on the Analyse board,
     [boardChart]): the range buttons (a full-width segmented control on phones), the expanded chart (16:9
     from 576 px; on a phone it fills the free height of the full-screen sheet — styles.css dash-sheet-fill / -item) -->
<ng-template #rangeBar>
  <div class="hz-range">
    <ng-container *ngTemplateOutlet="rangeButtons"></ng-container>
    <span *ngIf="rangeLoading" class="spinner-border spinner-border-sm text-muted d-none d-sm-inline-block" role="status"></span>
  </div>
</ng-template>

<!-- One line chart popup; context: data, options, id (the popup's view path = the chart's link id).
     The reset button undoes a drag / pinch zoom (or a zoom window from a link). -->
<ng-template #chartPopTpl let-data="data" let-options="options" let-id="id">
  <div class="dash-sheet-fill hz-sheet">
    <div class="hz-pop-bar"><ng-container *ngTemplateOutlet="rangeBar"></ng-container></div>
    <div class="hz-pop-chart dash-sheet-fill-item">
      <canvas baseChart #popChart="base-chart" [data]="data" [options]="options" type="line"></canvas>
      <button *ngIf="isZoomed(id)" type="button" class="btn btn-sm btn-outline-secondary dash-chart-reset"
              (click)="resetZoom(popChart)">{{ 'CHART_RESET' | translate }}</button>
    </div>
  </div>
</ng-template>

<ng-template #boilerExpandedTpl>
  <ng-container *ngTemplateOutlet="chartPopTpl; context: { data: boilerExpandedData, options: boilerExpandedOptions, id: views.boiler }"></ng-container>
</ng-template>
<ng-template #bufferExpandedTpl>
  <ng-container *ngTemplateOutlet="chartPopTpl; context: { data: bufferExpandedData, options: bufferExpandedOptions, id: views.buffer }"></ng-container>
</ng-template>
<ng-template #waterExpandedTpl>
  <ng-container *ngTemplateOutlet="chartPopTpl; context: { data: waterExpandedData, options: waterExpandedOptions, id: views.water }"></ng-container>
</ng-template>
<ng-template #solarThermalExpandedTpl>
  <ng-container *ngTemplateOutlet="chartPopTpl; context: { data: solarThermalExpandedData, options: solarThermalExpandedOptions, id: views.solarThermal }"></ng-container>
</ng-template>
<ng-template #outdoorExpandedTpl>
  <ng-container *ngTemplateOutlet="chartPopTpl; context: { data: outdoorExpandedData, options: outdoorExpandedOptions, id: views.outdoor }"></ng-container>
</ng-template>
<ng-template #pumpExpandedTpl>
  <ng-container *ngTemplateOutlet="chartPopTpl; context: { data: pumpExpandedData, options: pumpExpandedOptions, id: views.pump }"></ng-container>
</ng-template>
<ng-template #hydExpandedTpl>
  <ng-container *ngTemplateOutlet="chartPopTpl; context: { data: hydExpandedData, options: hydExpandedOptions, id: views.hydraulics }"></ng-container>
</ng-template>

<!-- Fröling settings (read-only): neutral hints, the settings by component, the change log -->
<ng-template #settingsTpl>
  <div class="hz-set" *ngIf="froelingSettings as fs">
    <div class="text-muted dash-meta mb-2">{{ 'FS_READONLY' | translate:{ time: fmtDateTime(fs.fetchedAt) } }}</div>
    <div *ngIf="settingHintList.length" class="hz-set-hints mb-2">
      <div class="fw-semibold">{{ 'FS_HINTS_TITLE' | translate }}</div>
      <ul>
        <li *ngFor="let h of settingHintList; trackBy: trackHint">{{ groupLabel(h.group) }}: {{ h.hint.key | translate:h.hint.params }}</li>
      </ul>
    </div>
    <ng-container *ngFor="let g of fs.components">
      <!-- the component in the UI language; the controller's own name (as in the Fröling app) muted beside it -->
      <h6 [title]="g.name">{{ groupIcon(g.type) }} {{ groupLabel(g) }}<span *ngIf="groupLabel(g) !== g.name"
          class="fw-normal ms-1 opacity-75" style="text-transform:none">· {{ g.name }}</span></h6>
      <div class="hz-set-row" *ngFor="let s of g.settings" [title]="s.label">
        <span class="min-w-0">{{ settingLabel(g, s) }}</span>
        <span class="v">{{ settingValue(s) }}</span>
        <div *ngIf="settingHint(g, s) as h" class="hz-set-hint">{{ 'FS_HINT_PREFIX' | translate }}: {{ h.key | translate:h.params }}</div>
      </div>
      <div class="hz-set-row" *ngIf="g.timeWindows?.length">
        <span class="min-w-0">{{ 'FS_TIME_WINDOWS' | translate }}</span>
        <span class="v"><div *ngFor="let l of windowLines(g)">{{ l }}</div></span>
      </div>
    </ng-container>
    <h6>📝 {{ 'FS_CHANGES' | translate }}</h6>
    <div *ngIf="!fs.changes.length" class="text-muted dash-meta">{{ 'FS_NO_CHANGES' | translate:{ since: fmtDateTime(fs.since) } }}</div>
    <div class="hz-set-log" *ngFor="let c of fs.changes">
      <span class="text-muted text-nowrap">{{ fmtDateTime(c.ts) }}</span>
      <span class="min-w-0" [title]="c.component">{{ groupLabel(changeGroup(c.componentId), c.component) }} · {{ changeText(c) }}</span>
    </div>
    <div *ngIf="fs.changes.length" class="text-muted dash-meta mt-1">{{ 'FS_LOG_SINCE' | translate:{ since: fmtDateTime(fs.since) } }}</div>
  </div>
</ng-template>

<!-- Pellet popup: the range's figures beside the range buttons (below them on a phone), bars + burn-rate line, hint -->
<ng-template #pelletExpandedTpl>
  <div class="dash-sheet-fill hz-sheet">
    <div class="hz-pop-bar">
      <div class="hz-pellet-stats text-muted">
        <span *ngIf="pelletRangeKg !== null">{{ rangeLabel }}: <span class="fw-semibold text-body">{{ pelletRangeKg | number:'1.0-1' }} kg</span></span>
        <span *ngIf="pelletRangeBurnH !== null">{{ 'HEIZUNG_PELLET_BRENNDAUER' | translate }}: {{ pelletRangeBurnH | number:'1.0-1' }} h</span>
        <span *ngIf="pelletRangeAvgRate !== null">Ø {{ pelletRangeAvgRate | number:'1.1-1' }} kg/h {{ 'HEIZUNG_PELLET_BEIM_BRENNEN' | translate }}</span>
        <span *ngIf="pelletUsage?.burning" class="text-danger fw-semibold">🔥 {{ 'HEIZUNG_PELLET_JETZT' | translate }} {{ pelletUsage!.currentRateKgH | number:'1.1-1' }} kg/h</span>
        <span *ngIf="pelletUsageLoading" class="spinner-border spinner-border-sm text-muted" role="status"></span>
      </div>
      <ng-container *ngTemplateOutlet="rangeBar"></ng-container>
    </div>
    <div class="hz-pop-chart hz-pellet-chart dash-sheet-fill-item">
      <canvas baseChart #pelletChart="base-chart" [data]="pelletUsageData" [options]="pelletUsageOptions" type="bar"></canvas>
      <button *ngIf="pelletZoomed" type="button" class="btn btn-sm btn-outline-secondary dash-chart-reset"
              (click)="resetPelletZoom(pelletChart)">{{ 'CHART_RESET' | translate }}</button>
    </div>
    <div class="text-muted mt-1 dash-meta">{{ (range === 'day' ? 'HEIZUNG_PELLET_HINT_DAY' : 'HEIZUNG_PELLET_HINT_LONG') | translate }}</div>
  </div>
</ng-template>

<!-- Cards: two columns (four from 992 px); a phone shows each card full width (styles.css dash-chart-col)
     with its name and value in one row and the chart below (hz-text, dash-card-chart) -->
<div class="row g-2 g-sm-3 mb-3" *ngIf="froeling" [class.section-flash]="froelingFlash">

  <!-- Kessel / Boiler -->
  <div class="col-6 col-lg-3 dash-chart-col">
    <app-popup-card cardClass="h-100 border-danger" popupMaxWidth="min(92vw, 1100px)" [linkId]="views.boiler" boardChart="froeling.boiler"
                    [popupTitle]="'🔥 ' + ('HEIZUNG_KESSEL_24H' | translate)"
                    [tapTpl]="froelingHistory.length >= 2 ? boilerExpandedTpl : undefined">
      <ng-template #body>
        <div class="card-body d-flex flex-column p-2 p-sm-3">
          <div class="d-flex align-items-center gap-2 flex-grow-1">
            <div class="card-icon fs-2 flex-shrink-0">🔥</div>
            <div class="min-w-0 flex-grow-1 hz-text">
              <div class="text-muted small hz-label">{{ 'HEIZUNG_KESSEL' | translate }}</div>
              <div class="card-value fs-5 fw-bold text-danger text-truncate hz-value">{{ froeling.boilerTempC | number:'1.1-1' }} °C</div>
              <div class="text-muted text-truncate m-wrap dash-meta">
                <span *ngIf="froeling.boilerState">{{ boilerStateLabelLive }}</span>
                <span *ngIf="froeling.exhaustTempC"> · {{ 'HEIZUNG_ABGAS' | translate }} {{ froeling.exhaustTempC | number:'1.0-0' }}°</span>
              </div>
              <div *ngIf="froeling.boilerStateCode != null" class="text-muted text-truncate m-wrap dash-meta">
                {{ 'HEIZUNG_SAUGZUG' | translate }} {{ froeling.draftFanPct | number:'1.0-0' }} % · O₂ {{ froeling.oxygenPct | number:'1.1-1' }} %
              </div>
              <div *ngIf="froeling.bufferPumpPct != null || froeling.ashHoursLeft != null" class="text-muted text-truncate m-wrap dash-meta">
                <span *ngIf="froeling.bufferPumpPct != null">{{ 'HEIZUNG_PUFFERPUMPE' | translate }} {{ froeling.bufferPumpPct }} %</span>
                <span *ngIf="froeling.ashHoursLeft != null"><span *ngIf="froeling.bufferPumpPct != null"> · </span><span [title]="'HEIZUNG_ASCHE_TITLE' | translate">{{ 'HEIZUNG_ASCHE' | translate:{ h: froeling.ashHoursLeft } }}</span></span>
              </div>
              <div *ngIf="froeling.boilerModulationPct" class="text-muted dash-meta">
                {{ 'HEIZUNG_MOD' | translate }} {{ froeling.boilerModulationPct | number:'1.0-0' }}%
                <span *ngIf="froeling.returnTempC"> · {{ 'HEIZUNG_RL' | translate }} {{ froeling.returnTempC | number:'1.0-0' }}°</span>
              </div>
            </div>
          </div>
          <div *ngIf="froelingHistory.length >= 2" class="hz-card-chart dash-card-chart">
            <canvas baseChart [data]="boilerChartData" [options]="boilerChartOptions" type="line"></canvas>
          </div>
        </div>
      </ng-template>
    </app-popup-card>
  </div>

  <!-- Pufferspeicher / Buffer -->
  <div class="col-6 col-lg-3 dash-chart-col">
    <app-popup-card cardClass="h-100 border-primary" popupMaxWidth="min(92vw, 1100px)" [linkId]="views.buffer" boardChart="froeling.buffer"
                    [popupTitle]="'🛢️ ' + ('HEIZUNG_PUFFER_24H' | translate)"
                    [tapTpl]="froelingHistory.length >= 2 ? bufferExpandedTpl : undefined">
      <ng-template #body>
        <div class="card-body d-flex flex-column p-2 p-sm-3">
          <div class="d-flex align-items-center gap-2 flex-grow-1">
            <div class="card-icon fs-2 flex-shrink-0">🛢️</div>
            <div class="min-w-0 flex-grow-1 hz-text">
              <div class="text-muted small hz-label">{{ 'HEIZUNG_PUFFER' | translate }}</div>
              <div class="d-flex align-items-baseline gap-1 hz-value">
                <div class="card-value fs-5 fw-bold text-danger text-truncate">{{ froeling.bufferTopTempC | number:'1.1-1' }}°</div>
                <span class="text-muted small">/</span>
                <span class="text-muted small">{{ froeling.bufferBottomTempC | number:'1.1-1' }}°</span>
              </div>
              <div *ngIf="froeling.bufferChargePct" class="mt-1">
                <div class="progress" style="height:5px;">
                  <div class="progress-bar"
                       [class.bg-success]="froeling.bufferChargePct >= 60"
                       [class.bg-warning]="froeling.bufferChargePct >= 30 && froeling.bufferChargePct < 60"
                       [class.bg-danger]="froeling.bufferChargePct < 30"
                       [style.width.%]="froeling.bufferChargePct"></div>
                </div>
                <div class="text-muted dash-meta">{{ froeling.bufferChargePct }}% {{ 'HEIZUNG_GELADEN' | translate }}</div>
              </div>
              <div *ngIf="froeling.bufferPumpPct != null" class="text-muted text-truncate dash-meta">
                {{ 'HEIZUNG_LADEPUMPE' | translate }} {{ froeling.bufferPumpPct }} %
              </div>
            </div>
          </div>
          <div *ngIf="froelingHistory.length >= 2" class="hz-card-chart dash-card-chart">
            <canvas baseChart [data]="bufferChartData" [options]="bufferChartOptions" type="line"></canvas>
          </div>
        </div>
      </ng-template>
    </app-popup-card>
  </div>

  <!-- Heizkreise -->
  <div class="col-6 col-lg-3 dash-chart-col" *ngFor="let hc of froeling.heatingCircuits; let i = index; trackBy: trackHc">
    <app-popup-card cardClass="h-100 border-success" popupMaxWidth="min(92vw, 1100px)" [linkId]="hcView(hc.name)"
                    [boardChart]="'froeling.hc.' + hc.name"
                    [popupTitle]="'🌡️ ' + hcLabel(hc.name)"
                    [tapTpl]="froelingHistory.length >= 2 && hcExpandedData[hc.name] ? hcExpandedTplRef : undefined">
      <ng-template #body>
        <div class="card-body d-flex flex-column p-2 p-sm-3">
          <div class="d-flex align-items-center gap-2 flex-grow-1">
            <div class="card-icon fs-2 flex-shrink-0">🌡️</div>
            <div class="min-w-0 flex-grow-1 hz-text">
              <div class="text-muted small text-truncate hz-label">{{ hcLabel(hc.name) }}</div>
              <div class="d-flex align-items-baseline gap-1 hz-value">
                <div class="card-value fs-5 fw-bold text-truncate" [style.color]="hcColor(i)">{{ hc.flowTempActualC | number:'1.1-1' }}°</div>
                <span *ngIf="hc.flowTempTargetC != null" class="hz-target text-nowrap" [style.color]="setpointColor">/ {{ hc.flowTempTargetC | number:'1.0-1' }}° {{ 'HEIZUNG_SOLL' | translate }}</span>
              </div>
              <div *ngIf="hc.pumpState" class="text-muted text-truncate m-wrap dash-meta">
                {{ 'HEIZUNG_PUMPE' | translate }}: {{ pumpLabel(hc.pumpState) }}<span *ngIf="hc.modeText"> · {{ hc.modeText }}</span>
              </div>
              <div *ngIf="hc.flowTempAtMinus10C != null && hc.flowTempAtPlus10C != null" class="text-muted text-truncate m-wrap dash-meta"
                   [title]="'HEIZUNG_KURVE_HINT' | translate">
                {{ 'HEIZUNG_KURVE' | translate:{ m: hc.flowTempAtMinus10C, p: hc.flowTempAtPlus10C } }}
              </div>
            </div>
          </div>
          <div *ngIf="froelingHistory.length >= 2 && hcChartData[hc.name]" class="hz-card-chart dash-card-chart">
            <canvas baseChart [data]="hcChartData[hc.name]" [options]="hcChartOptions" type="line"></canvas>
          </div>
        </div>
      </ng-template>
    </app-popup-card>

    <ng-template #hcExpandedTplRef>
      <ng-container *ngTemplateOutlet="chartPopTpl; context: { data: hcExpandedData[hc.name], options: hcExpandedOptions[hc.name], id: hcView(hc.name) }"></ng-container>
    </ng-template>
  </div>

  <!-- Warmwasser -->
  <div class="col-6 col-lg-3 dash-chart-col">
    <app-popup-card cardClass="h-100 border-info" popupMaxWidth="min(92vw, 1100px)" [linkId]="views.water" boardChart="froeling.water"
                    [popupTitle]="'🚿 ' + ('HEIZUNG_WARMWASSER_24H' | translate)"
                    [tapTpl]="froelingHistory.length >= 2 ? waterExpandedTpl : undefined">
      <ng-template #body>
        <div class="card-body d-flex flex-column p-2 p-sm-3">
          <div class="d-flex align-items-center gap-2 flex-grow-1">
            <div class="card-icon fs-2 flex-shrink-0">🚿</div>
            <div class="min-w-0 flex-grow-1 hz-text">
              <div class="text-muted small hz-label">{{ 'HEIZUNG_WARMWASSER' | translate }}</div>
              <div class="card-value fs-5 fw-bold text-info text-truncate hz-value">{{ froeling.hotWaterTempC | number:'1.1-1' }} °C</div>
              <div *ngIf="froeling.dhwPumpPct != null || froeling.dhwSolarRefTempC != null" class="text-muted text-truncate m-wrap dash-meta">
                <span *ngIf="froeling.dhwPumpPct != null">{{ 'HEIZUNG_LADEPUMPE' | translate }} {{ froeling.dhwPumpPct }} %</span>
                <span *ngIf="froeling.dhwSolarRefTempC != null"><span *ngIf="froeling.dhwPumpPct != null"> · </span>{{ 'HEIZUNG_UNTEN' | translate }} {{ froeling.dhwSolarRefTempC | number:'1.0-0' }}°</span>
              </div>
              <div *ngIf="froeling.dhwSetTempC != null" class="text-muted text-truncate m-wrap dash-meta">
                {{ 'HEIZUNG_WW_SOLL' | translate:{ set: froeling.dhwSetTempC, reload: froeling.dhwReloadBelowC ?? '–' } }}
              </div>
            </div>
          </div>
          <div *ngIf="froelingHistory.length >= 2" class="hz-card-chart dash-card-chart">
            <canvas baseChart [data]="waterChartData" [options]="waterChartOptions" type="line"></canvas>
          </div>
        </div>
      </ng-template>
    </app-popup-card>
  </div>

  <!-- Zirkulationspumpe -->
  <div class="col-6 col-lg-3 dash-chart-col">
    <app-popup-card cardClass="h-100 border-primary" popupMaxWidth="min(92vw, 1100px)" [linkId]="views.pump" boardChart="froeling.pump"
                    [popupTitle]="'🔄 ' + ('HEIZUNG_ZIRKPUMPE' | translate)"
                    [tapTpl]="froelingHistory.length >= 2 ? pumpExpandedTpl : undefined">
      <ng-template #body>
        <div class="card-body d-flex flex-column p-2 p-sm-3">
          <div class="d-flex align-items-center gap-2 flex-grow-1">
            <div class="card-icon fs-2 flex-shrink-0">🔄</div>
            <div class="min-w-0 flex-grow-1 hz-text">
              <div class="text-muted small hz-label">{{ 'HEIZUNG_ZIRKPUMPE' | translate }}</div>
              <div class="card-value fs-5 fw-bold text-primary text-truncate hz-value">{{ froeling.circulationPumpPct | number:'1.0-0' }} %</div>
              <div class="text-muted dash-meta">{{ 'HEIZUNG_DREHZAHL' | translate }}<span *ngIf="circReturnLabel"> · {{ 'HEIZUNG_RUECKLAUF' | translate }}: {{ circReturnLabel }}</span></div>
            </div>
          </div>
          <div *ngIf="froelingHistory.length >= 2" class="hz-card-chart dash-card-chart">
            <canvas baseChart [data]="pumpChartData" [options]="pumpChartOptions" type="line"></canvas>
          </div>
        </div>
      </ng-template>
    </app-popup-card>
  </div>

  <!-- Solarthermie -->
  <div class="col-6 col-lg-3 dash-chart-col" *ngIf="froeling.solarThermalTempC != null">
    <app-popup-card cardClass="h-100 border-warning" popupMaxWidth="min(92vw, 1100px)" [linkId]="views.solarThermal" boardChart="froeling.solarThermal"
                    [popupTitle]="'☀️ ' + ('HEIZUNG_SOLAR_24H' | translate)"
                    [tapTpl]="froelingHistory.length >= 2 ? solarThermalExpandedTpl : undefined">
      <ng-template #body>
        <div class="card-body d-flex flex-column p-2 p-sm-3">
          <div class="d-flex align-items-center gap-2 flex-grow-1">
            <div class="card-icon fs-2 flex-shrink-0">☀️</div>
            <div class="min-w-0 flex-grow-1 hz-text">
              <div class="text-muted small hz-label">{{ 'HEIZUNG_SOLAR' | translate }}</div>
              <div class="card-value fs-5 fw-bold text-warning text-truncate hz-value">{{ froeling.solarThermalTempC | number:'1.1-1' }} °C</div>
              <div *ngIf="froeling.collectorFlowTempC != null" class="text-muted text-truncate m-wrap dash-meta">
                {{ 'HEIZUNG_VL' | translate }} {{ froeling.collectorFlowTempC | number:'1.0-0' }}° · {{ 'HEIZUNG_RL' | translate }} {{ froeling.collectorReturnTempC | number:'1.0-0' }}°
              </div>
              <div *ngIf="froeling.solarThermalPumpPct || froeling.collectorFlowTempC != null" class="text-muted text-truncate m-wrap dash-meta">
                {{ 'HEIZUNG_PUMPE' | translate }} {{ froeling.solarThermalPumpPct | number:'1.0-0' }} %<span
                  *ngIf="froeling.solarYield?.yesterdayKwh != null"> · {{ 'HEIZUNG_SOLAR_GESTERN' | translate:{ kwh: froeling.solarYield!.yesterdayKwh } }}</span>
              </div>
            </div>
          </div>
          <div *ngIf="froelingHistory.length >= 2" class="hz-card-chart dash-card-chart">
            <canvas baseChart [data]="solarThermalChartData" [options]="solarThermalChartOptions" type="line"></canvas>
          </div>
        </div>
      </ng-template>
    </app-popup-card>
  </div>

  <!-- Außentemperatur (always shown — 0 °C is a legitimate freezing reading) -->
  <div class="col-6 col-lg-3 dash-chart-col">
    <app-popup-card cardClass="h-100" popupMaxWidth="min(92vw, 1100px)" [linkId]="views.outdoor" boardChart="froeling.outdoor"
                    [popupTitle]="'🌤️ ' + ('HEIZUNG_AUSSEN_24H' | translate)"
                    [tapTpl]="froelingHistory.length >= 2 ? outdoorExpandedTpl : undefined">
      <ng-template #body>
        <div class="card-body d-flex flex-column p-2 p-sm-3">
          <div class="d-flex align-items-center gap-2 flex-grow-1">
            <div class="card-icon fs-2 flex-shrink-0">🌤️</div>
            <div class="min-w-0 flex-grow-1 hz-text">
              <div class="text-muted small hz-label">{{ 'HEIZUNG_AUSSEN' | translate }}</div>
              <div class="card-value fs-5 fw-bold text-truncate hz-value"
                   [class.text-info]="froeling.outdoorTempC < 10"
                   [class.text-success]="froeling.outdoorTempC >= 10 && froeling.outdoorTempC < 25"
                   [class.text-warning]="froeling.outdoorTempC >= 25">
                {{ froeling.outdoorTempC | number:'1.1-1' }} °C
              </div>
            </div>
          </div>
          <div *ngIf="froelingHistory.length >= 2" class="hz-card-chart dash-card-chart">
            <canvas baseChart [data]="outdoorChartData" [options]="outdoorChartOptions" type="line"></canvas>
          </div>
        </div>
      </ng-template>
    </app-popup-card>
  </div>

  <!-- Pellets: kg today, burn rate, last 24 h, the boiler's own hopper (not the store), 7-day bars -->
  <div class="col-6 col-lg-3 dash-chart-col">
    <app-popup-card cardClass="h-100" popupMaxWidth="min(92vw, 1100px)" [linkId]="views.pellet" boardChart="pellets"
                    [popupTitle]="'🪵 ' + ('HEIZUNG_PELLET_CHART_TITLE' | translate)"
                    [tapTpl]="pelletStats.length > 0 ? pelletExpandedTpl : undefined"
                    (tappedChange)="onPelletOverlay($event)">
      <ng-template #body>
        <div class="card-body d-flex flex-column p-2 p-sm-3">
          <div class="d-flex align-items-center gap-2 flex-grow-1">
            <div class="card-icon fs-2 flex-shrink-0">🪵</div>
            <div class="min-w-0 flex-grow-1 hz-text">
              <div class="text-muted small hz-label">{{ 'HEIZUNG_PELLETS' | translate }}</div>
              <div class="d-flex align-items-baseline gap-1 hz-value">
                <div class="card-value fs-5 fw-bold text-truncate">{{ pelletTodayKg | number:'1.0-1' }} kg</div>
                <span class="text-muted dash-meta">{{ 'HEIZUNG_PELLET_HEUTE' | translate }}</span>
              </div>
              <div class="text-truncate m-wrap dash-meta">
                <span *ngIf="pelletUsage?.burning" class="text-danger fw-semibold">🔥 {{ pelletUsage!.currentRateKgH | number:'1.1-1' }} kg/h</span>
                <span *ngIf="pelletUsage && !pelletUsage.burning" class="text-muted">{{ 'HEIZUNG_PELLET_AUS' | translate }}</span>
                <span class="text-muted"><span *ngIf="pelletUsage"> · </span>{{ pellet24hKg | number:'1.0-0' }} kg / 24 h</span>
              </div>
              <div class="text-muted text-truncate mt-1 dash-meta" [title]="'HEIZUNG_PELLET_BEHAELTER_HINT' | translate">
                {{ 'HEIZUNG_PELLET_BEHAELTER' | translate }} {{ froeling.pelletLevelPct }} %
              </div>
              <div class="progress" style="height:3px;" [title]="'HEIZUNG_PELLET_BEHAELTER_HINT' | translate">
                <div class="progress-bar bg-secondary" [style.width.%]="froeling.pelletLevelPct"></div>
              </div>
              <div *ngIf="froeling.storeRemainingT != null" class="text-muted text-truncate m-wrap mt-1 dash-meta" [title]="'HEIZUNG_LAGER_HINT' | translate">
                {{ 'HEIZUNG_LAGER' | translate }} {{ froeling.storeRemainingT | number:'1.1-1' }} t<span
                  *ngIf="storeDays != null"> · {{ 'HEIZUNG_LAGER_TAGE' | translate:{ days: storeDays, kg: (recentKgPerDay | number:'1.0-0') } }}</span>
              </div>
            </div>
          </div>
          <div *ngIf="pelletStats.length > 0" class="hz-card-chart dash-card-chart">
            <canvas baseChart [data]="pelletChartData" [options]="pelletChartOptions" type="bar"></canvas>
          </div>
        </div>
      </ng-template>
    </app-popup-card>
  </div>

  <!-- Fröling settings (read-only) with their change log -->
  <div class="col-6 col-lg-3 dash-chart-col" *ngIf="froelingSettings">
    <app-popup-card cardClass="h-100" popupMaxWidth="min(92vw, 760px)" [linkId]="views.settings"
                    [popupTitle]="'⚙️ ' + ('FS_TITLE' | translate)" [tapTpl]="settingsTpl">
      <ng-template #body>
        <div class="card-body d-flex flex-column p-2 p-sm-3">
          <div class="d-flex align-items-center gap-2 flex-grow-1">
            <div class="card-icon fs-2 flex-shrink-0">⚙️</div>
            <div class="min-w-0 flex-grow-1 hz-text">
              <div class="text-muted small hz-label">{{ 'FS_TITLE' | translate }}</div>
              <div class="card-value fs-5 fw-bold text-truncate hz-value" *ngIf="froeling.boilerSetTempC != null">{{ froeling.boilerSetTempC | number:'1.0-0' }} °C</div>
              <div class="text-muted text-truncate m-wrap dash-meta" *ngIf="froeling.boilerSetTempC != null">{{ 'FS_CARD_BOILER_SET' | translate }}<span
                *ngIf="froeling.boilerModeText"> · {{ froeling.boilerModeText }}</span></div>
              <div class="text-muted text-truncate m-wrap dash-meta">
                <span *ngIf="lastSettingChange; else noChange">{{ 'FS_CARD_LAST_CHANGE' | translate:{ time: fmtDateTime(lastSettingChange) } }}</span>
                <ng-template #noChange>{{ 'FS_CARD_NO_CHANGE' | translate }}</ng-template>
              </div>
              <div *ngIf="settingHintList.length" class="text-muted text-truncate m-wrap dash-meta">{{ 'FS_CARD_HINTS' | translate:{ n: settingHintList.length } }}</div>
            </div>
          </div>
        </div>
      </ng-template>
    </app-popup-card>
  </div>

  <!-- Hydraulik: where the heat goes — boiler, buffer, circuits (Ist / Soll), DHW tank, pumps and burner on one axis -->
  <div class="col-12 col-lg-6 dash-chart-col">
    <app-popup-card cardClass="h-100 border-secondary" popupMaxWidth="min(96vw, 1300px)" [linkId]="views.hydraulics" boardChart="froeling.hydraulics"
                    [popupTitle]="'🧭 ' + ('HYD_TITLE' | translate)"
                    [tapTpl]="froelingHistory.length >= 2 ? hydExpandedTpl : undefined">
      <ng-template #body>
        <div class="card-body d-flex flex-column p-2 p-sm-3">
          <div class="d-flex align-items-center gap-2">
            <div class="card-icon fs-2 flex-shrink-0">🧭</div>
            <div class="min-w-0 flex-grow-1 hz-text">
              <div class="text-muted small hz-label">{{ 'HYD_TITLE' | translate }}</div>
              <div class="text-muted text-truncate m-wrap dash-meta">{{ 'HYD_CARD_HINT' | translate }}</div>
            </div>
          </div>
          <div *ngIf="froelingHistory.length >= 2" class="hz-card-chart hz-hyd-chart dash-card-chart">
            <canvas baseChart [data]="hydChartData" [options]="hydChartOptions" type="line"></canvas>
          </div>
        </div>
      </ng-template>
    </app-popup-card>
  </div>

</div>

<!-- Skeleton loader (full-width tiles on a phone, like the cards) -->
<div *ngIf="!froeling" class="row g-2 g-sm-3 mb-3">
  <div class="col-6 col-lg-3 dash-chart-col" *ngFor="let _ of [0,1,2,3,4,5]">
    <div class="card h-100">
      <div class="card-body d-flex align-items-center gap-2 p-2 p-sm-3">
        <app-skeleton width="2rem" height="2rem" radius="6px" [darkMode]="darkMode" style="flex-shrink:0;"></app-skeleton>
        <div style="flex:1;min-width:0;">
          <app-skeleton width="60%" height=".75rem" radius="4px" [darkMode]="darkMode" style="display:block;margin-bottom:6px;"></app-skeleton>
          <app-skeleton width="80%" height="1.4rem" radius="4px" [darkMode]="darkMode" style="display:block;"></app-skeleton>
        </div>
      </div>
    </div>
  </div>
</div>
